import { useCallback, useEffect, useMemo, useState } from "react";
import { getMatchIndexes, normalizeSearchValue } from "../utils/demandStudioSearchUtils";

const buildSearchMatches = ({ query, sectionDefinitions, sectionTexts }) => {
  if (!query) {
    return [];
  }

  const matches = [];
  sectionDefinitions.forEach((section) => {
    const sectionText = normalizeSearchValue(sectionTexts?.[section.id]);
    const sectionMatchIndexes = getMatchIndexes({ source: sectionText, query });
    sectionMatchIndexes.forEach((matchStartIndex) => {
      matches.push({
        type: "section",
        sectionId: section.id,
        matchStartIndex,
      });
    });
  });

  return matches;
};

const getNextIndex = ({ currentIndex, total, direction }) => {
  if (total <= 0) {
    return -1;
  }
  if (currentIndex < 0) {
    return 0;
  }
  if (direction === "previous") {
    return (currentIndex - 1 + total) % total;
  }
  return (currentIndex + 1) % total;
};

const useDemandStudioSearch = ({
  isOpen,
  showPreview,
  sectionDefinitions,
  sectionTexts,
  onJumpToSection,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);

  const normalizedSearchTerm = useMemo(
    () => normalizeSearchValue(searchTerm),
    [searchTerm]
  );

  const matches = useMemo(
    () =>
      buildSearchMatches({
        query: normalizedSearchTerm,
        sectionDefinitions,
        sectionTexts,
      }),
    [normalizedSearchTerm, sectionDefinitions, sectionTexts]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setActiveMatchIndex(-1);
    }
  }, [isOpen]);

  const jumpToMatch = useCallback(
    ({ index, shouldFocusMatch }) => {
      if (!isOpen || showPreview || index < 0) {
        return;
      }
      const currentMatch = matches[index];
      if (!currentMatch) {
        return;
      }
      const jumpPayload = {
        sectionId: currentMatch.sectionId,
        searchTerm: normalizedSearchTerm,
        shouldFocusMatch,
        matchStartIndex: currentMatch.matchStartIndex,
      };
      onJumpToSection?.(jumpPayload);
    },
    [isOpen, showPreview, matches, normalizedSearchTerm, onJumpToSection]
  );

  useEffect(() => {
    if (!normalizedSearchTerm || showPreview) {
      setActiveMatchIndex(-1);
      return;
    }
    const firstMatchIndex = matches.length ? 0 : -1;
    setActiveMatchIndex(firstMatchIndex);
    if (firstMatchIndex >= 0) {
      jumpToMatch({ index: firstMatchIndex, shouldFocusMatch: false });
    }
  }, [normalizedSearchTerm, matches.length, showPreview, jumpToMatch]);

  const navigateMatch = useCallback(
    ({ direction, shouldFocusMatch = true }) => {
      if (!matches.length) {
        return;
      }
      const nextIndex = getNextIndex({
        currentIndex: activeMatchIndex,
        total: matches.length,
        direction,
      });
      setActiveMatchIndex(nextIndex);
      jumpToMatch({ index: nextIndex, shouldFocusMatch });
    },
    [matches.length, activeMatchIndex, jumpToMatch]
  );

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      navigateMatch({
        direction: event.shiftKey ? "previous" : "next",
        shouldFocusMatch: true,
      });
    },
    [navigateMatch]
  );

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveMatchIndex(-1);
  }, []);

  return {
    searchTerm,
    onSearchChange: handleSearchChange,
    onSearchKeyDown: handleSearchKeyDown,
    clearSearch,
    hasSearch: Boolean(normalizedSearchTerm),
    matchCount: matches.length,
    activeMatchNumber: activeMatchIndex >= 0 ? activeMatchIndex + 1 : 0,
    activeMatch: activeMatchIndex >= 0 ? matches[activeMatchIndex] || null : null,
    goToNextMatch: () => navigateMatch({ direction: "next", shouldFocusMatch: true }),
    goToPreviousMatch: () =>
      navigateMatch({ direction: "previous", shouldFocusMatch: true }),
  };
};

export default useDemandStudioSearch;
